import { Icon, spacing, Text } from '@scality/core-ui';
import { Box, Button } from '@scality/core-ui/dist/next';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { humanizeToolName } from '../../mcp/toolCallDisplay';
import type { ToolApprovalRequest } from '../../types/ClientMessage';

const Prompt = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.r12};
  padding: ${spacing.r8} ${spacing.r12};
  border-left: 3px solid ${(props) => props.theme.statusWarning};
  border-radius: ${spacing.r4};
  white-space: normal;
`;

const PromptIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 1.3em;
`;

const ResourceList = styled.ul`
  margin: ${spacing.r4} 0 0;
  padding: ${spacing.r8} ${spacing.r12};
  list-style: none;
  font-family: 'Fira Code', monospace;
  font-size: 0.8rem;
  line-height: 1.7;
  color: ${(props) => props.theme.textPrimary};
  background: ${(props) => props.theme.backgroundLevel3};
  border: 1px solid ${(props) => props.theme.backgroundLevel4};
  border-radius: ${spacing.r4};
  max-height: 12rem;
  overflow-y: auto;
  overflow-wrap: anywhere;
`;

type ToolApprovalBannerProps = {
  approval: ToolApprovalRequest;
  onApprove: () => void;
  onDeny: () => void;
};

const describe = ({ toolName, explanations, callCount }: ToolApprovalRequest) => {
  if (explanations.length === 1 && callCount === 1) return explanations[0];
  if (explanations.length > 0) return explanations.join(' ');
  return `Guardian would like to use ${humanizeToolName(toolName).toLowerCase()}.`;
};

/**
 * Asks the user to approve one tool type for the current request. A single decision
 * covers every call of that tool in the turn; the resources it acts on are listed behind
 * a toggle, collapsed by default — the tool's raw arguments are never shown.
 */
export const ToolApprovalBanner: React.FC<ToolApprovalBannerProps> = ({ approval, onApprove, onDeny }) => {
  const [showResources, setShowResources] = useState(false);
  const toggleResources = useCallback(() => setShowResources((shown) => !shown), []);
  const { resources } = approval;

  return (
    <Prompt role="group" aria-label="Approval needed">
      <PromptIcon>
        <Icon name="Exclamation-circle" size="lg" color="statusWarning" />
      </PromptIcon>
      <Box display="flex" flexDirection="column" gap={spacing.r8} flex={1} minWidth={0}>
        <Text color="textPrimary">{describe(approval)}</Text>
        {resources.length > 0 && (
          <div>
            <Button
              size="inline"
              variant="outline"
              icon={<Icon name={showResources ? 'Chevron-up' : 'Chevron-down'} />}
              label={`${showResources ? 'Hide' : 'Show'} ${resources.length === 1 ? 'resource' : `${resources.length} resources`}`}
              aria-expanded={showResources}
              onClick={toggleResources}
            />
            {showResources && (
              <ResourceList>
                {resources.map((resource) => (
                  <li key={resource}>{resource}</li>
                ))}
              </ResourceList>
            )}
          </div>
        )}
        <Box display="flex" gap={spacing.r8} justifyContent="flex-end">
          <Button label="Deny" variant="outline" onClick={onDeny} />
          <Button label="Approve" variant="primary" onClick={onApprove} />
        </Box>
      </Box>
    </Prompt>
  );
};
